import { memo, useState } from "react";
import { Loader2Icon, XCircleIcon } from "lucide-react";
import type { ToolCallMessagePartStatus } from "@assistant-ui/react";
import { cn } from "@/lib/utils";

export interface ToolChipProps {
  /** Icon shown in the icon box */
  icon: React.ElementType;
  /** Bold verb while running (e.g. "Adding...") */
  runningVerb: string;
  /** Bold verb when complete (e.g. "Added") */
  completeVerb: string;
  /** Bold verb when the result contains an error (e.g. "Failed to add node") */
  failedVerb?: string;
  /** Optional muted badge next to the verb (e.g. "Text Prompt") */
  subject?: string;
  status: ToolCallMessagePartStatus | undefined;
  /** If provided, the row is clickable */
  onChipClick?: () => void;
  /** Raw args JSON string — shown in the ⋮ panel */
  argsText?: string;
  /** Raw result — shown in the ⋮ panel */
  result?: unknown;
  /** Muted style for read-only tools (same layout, lower opacity) */
  muted?: boolean;
  /** Error message from the tool result (e.g. result.error) — shows red X icon + failedVerb */
  resultError?: string | null;
}

export const ToolChip = memo(function ToolChip({
  icon: Icon,
  runningVerb,
  completeVerb,
  failedVerb,
  subject,
  status,
  onChipClick,
  argsText,
  result,
  muted = false,
  resultError,
}: ToolChipProps) {
  const [expanded, setExpanded] = useState(false);

  const statusType = status?.type ?? "complete";
  const isRunning = statusType === "running";
  // Hard error from the runtime (aborted, timed out, etc.) — full red treatment
  const isStatusError =
    statusType === "incomplete" &&
    (status as { type: "incomplete"; reason?: string }).reason !== "cancelled";
  // Soft error from the tool result body — subtle: only the X icon is red
  const hasResultError = !!resultError;
  // Either type blocks clicking
  const isError = isStatusError || hasResultError;

  const verb = isRunning
    ? runningVerb
    : hasResultError
      ? (failedVerb ?? "Failed")
      : completeVerb;

  const hasDetails = !muted && (argsText !== undefined || result !== undefined);

  const errorText =
    isStatusError &&
    status?.type === "incomplete" &&
    "error" in status &&
    status.error
      ? typeof status.error === "string"
        ? status.error
        : JSON.stringify(status.error)
      : null;

  // Normalize result for display (may arrive as a JSON string)
  const displayResult =
    typeof result === "string"
      ? (() => {
          try {
            return JSON.stringify(JSON.parse(result), null, 2);
          } catch {
            return result;
          }
        })()
      : JSON.stringify(result, null, 2);

  return (
    <div className="flex flex-col gap-1.5 my-2 w-full">
      <div
        className={cn(
          "group flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[11px] transition-colors",
          // Status errors get a red hover; result errors and normal both use the neutral hover
          isStatusError
            ? "hover:bg-red-50/60 dark:hover:bg-red-500/15"
            : "hover:bg-muted/40 dark:hover:bg-white/8",
          !isRunning && !isError && onChipClick && "cursor-pointer",
        )}
        onClick={
          !isRunning && !isError && onChipClick ? onChipClick : undefined
        }
        role={onChipClick && !isRunning && !isError ? "button" : undefined}
        tabIndex={onChipClick && !isRunning && !isError ? 0 : undefined}
        onKeyDown={
          onChipClick && !isRunning && !isError
            ? (e) => e.key === "Enter" && onChipClick()
            : undefined
        }
      >
        {/* Icon box */}
        <div
          className={cn(
            "shrink-0 flex size-5 items-center justify-center rounded",
            isStatusError
              ? "bg-red-100 dark:bg-red-500/20 text-red-500"
              : hasResultError
                ? "bg-muted/70 dark:bg-muted/40 text-red-500" // normal bg, red icon only
                : muted
                  ? "bg-muted/50 text-muted-foreground/40"
                  : "bg-muted/70 dark:bg-muted/40 text-muted-foreground",
          )}
        >
          {isRunning ? (
            <Loader2Icon className="size-3 animate-spin" />
          ) : isError ? (
            <XCircleIcon className="size-3" />
          ) : (
            <Icon className="size-3" />
          )}
        </div>

        {/* Verb */}
        <span
          className={cn(
            "font-medium",
            isStatusError
              ? "text-red-600 dark:text-red-400"
              : muted
                ? "text-muted-foreground/50"
                : isRunning
                  ? "text-muted-foreground"
                  : "text-foreground/90",
          )}
        >
          {verb}
        </span>

        {/* Subject badge (success only) */}
        {subject && !isRunning && !isError && (
          <span className="rounded border border-border/40 bg-background/50 dark:bg-background/20 px-1.5 py-0.5 text-[10px] text-muted-foreground">
            {subject}
          </span>
        )}

        {/* Details toggle — revealed on row hover, pushed to far right */}
        {hasDetails && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            className="ml-auto opacity-0 group-hover:opacity-100 text-muted-foreground/50 hover:text-muted-foreground transition-all leading-none"
            aria-label="Show details"
          >
            ⋮
          </button>
        )}
      </div>

      {/* Expanded details panel */}
      {expanded && hasDetails && (
        <div className="rounded-lg border border-border/40 bg-muted/30 px-3 py-2 text-[11px] font-mono text-muted-foreground overflow-x-auto">
          {argsText && (
            <div>
              <span className="text-[9px] uppercase tracking-wide text-muted-foreground/50 block mb-1">
                Args
              </span>
              <pre className="whitespace-pre-wrap break-all">{argsText}</pre>
            </div>
          )}
          {result !== undefined && (
            <div
              className={argsText ? "mt-2 pt-2 border-t border-border/30" : ""}
            >
              <span className="text-[9px] uppercase tracking-wide text-muted-foreground/50 block mb-1">
                Result
              </span>
              <pre className="whitespace-pre-wrap break-all">
                {displayResult}
              </pre>
            </div>
          )}
          {errorText && (
            <div
              className={
                argsText || result !== undefined
                  ? "mt-2 pt-2 border-t border-border/30"
                  : ""
              }
            >
              <span className="text-[9px] uppercase tracking-wide block mb-1">
                Error
              </span>
              <pre className="whitespace-pre-wrap break-all">{errorText}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
